/**
 * Role-based Route Guard
 * Protects routes based on user roles
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: string[];
  fallback?: React.ReactNode;
  redirectTo?: string;
}

const RoleGuard: React.FC<RoleGuardProps> = ({ 
  children, 
  allowedRoles, 
  fallback,
  redirectTo = '/unauthorized' 
}) => {
  const { user, isAuthenticated } = useAuthStore();

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required role (case-insensitive comparison)
  const hasRequiredRole = user && allowedRoles.some(
    role => role.toLowerCase() === user.role.toLowerCase()
  );

  if (!hasRequiredRole) {
    // Show fallback or redirect to unauthorized page
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default RoleGuard;